var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useMemo } from 'react';
import { useStyles } from './EditAccount.styles';
import Wrapper from '../../Components/Wrapper/Wrapper';
import { useUserContext } from '../../Context/UserContext';
import { useApi } from '../../Context/APIContext';
import { useSnackbar } from 'notistack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Field from '../../Components/Field/Field';
import EmailField from '../../Components/Field/EmailField';
import Loading from '../../Components/Loading/Loading';
import Button from '@mui/material/Button';
import RightArrowIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import SuccessIcon from '@mui/icons-material/CheckCircleRounded';
import { Link } from 'react-router-dom';
import useApiAbort from '../../Api/Core/useApiAbort';
import PasswordField from '../../Components/Field/PasswordField';
import CreditCard from '../../Components/CreditCard';
import HTTP401 from '../../Features/Errors/HTTP401';
import Box from '@mui/material/Box';
const EditAccount = () => {
    const classes = useStyles();
    const { user, refreshUser, loggedIn } = useUserContext();
    const api = useApi();
    const abortController = useApiAbort();
    if (api) {
        api.setAbortController(abortController.current);
    }
    const { enqueueSnackbar } = useSnackbar();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [email, setEmail] = useState('');
    const [newVerificationEmail, setNewVerificationEmail] = useState(false);
    useEffect(() => {
        if (user) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
            setEmail(user.email || '');
        }
    }, [user]);
    const [saving, setSaving] = useState(false);
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        if (api && isFormValid) {
            setSaving(true);
            try {
                if (firstName !== user.firstName || lastName !== user.lastName || email !== user.email) {
                    yield api.editProfile({
                        firstName: firstName,
                        lastName: lastName,
                        email: email,
                    });
                    yield refreshUser();
                }
                if (newPassword !== '' && (newPassword === confirmNewPassword && currentPassword !== '')) {
                    yield api.changePassword({
                        newPassword: newPassword,
                        confirmNewPassword: confirmNewPassword,
                        currentPassword: currentPassword,
                    });
                    setNewPassword('');
                    setCurrentPassword('');
                    setConfirmNewPassword('');
                }
            }
            catch (exception) {
                enqueueSnackbar('Någonting gick fel. Kunde inte spara kontouppgifterna', { variant: 'error' });
                if (!abortController.current.signal.aborted) {
                    setSaving(false);
                    yield refreshUser();
                    return;
                }
            }
            if (!abortController.current.signal.aborted) {
                setSaving(false);
            }
            enqueueSnackbar('Dina kontouppgifter är nu sparade', { variant: 'success' });
        }
    });
    const sendNewVerificationMail = () => __awaiter(void 0, void 0, void 0, function* () {
        if (api) {
            yield api.initEmailVerification();
            setNewVerificationEmail(true);
        }
    });
    const isFormValid = useMemo(() => {
        return ((user &&
            firstName !== '' &&
            lastName !== '' &&
            email !== '' &&
            (firstName !== user.firstName || lastName !== user.lastName || email !== user.email) &&
            (newPassword === '' || (newPassword === confirmNewPassword && currentPassword !== ''))) ||
            (newPassword !== '' && (newPassword === confirmNewPassword && currentPassword !== '')));
    }, [firstName, lastName, email, user, newPassword, confirmNewPassword, currentPassword]);
    const buttonLink = React.forwardRef((props, ref) => (React.createElement(Link, Object.assign({}, props, { ref: ref }))));
    return ((!loggedIn && React.createElement(HTTP401, null)) || (React.createElement(Wrapper, null,
        React.createElement(Box, { component: "div", sx: classes.breadcrumb },
            React.createElement(Typography, { sx: classes.breadcrumbText, variant: "h4" }, "Mina sidor"),
            React.createElement(RightArrowIcon, null),
            React.createElement(Typography, { sx: classes.breadcrumbText, variant: "h4" }, "Kontouppgifter")),
        React.createElement(Typography, { variant: "h1", sx: classes.title }, "Kontouppgifter"),
        React.createElement(Box, { component: "form", sx: classes.form, onSubmit: handleSave, autoComplete: "off" },
            React.createElement(Field, { value: firstName, onChange: event => setFirstName(event.target.value), name: "firstname", label: "F\u00F6rnamn", placeholder: "Fyll i f\u00F6rnamn", isDisabled: user.hasStrongIdentification, required: true }),
            React.createElement(Field, { value: lastName, onChange: event => setLastName(event.target.value), name: "lastname", label: "Efternamn", placeholder: "Fyll i efternamn", isDisabled: user.hasStrongIdentification, required: true }),
            React.createElement(EmailField, { id: "edit-mail", autoComplete: "none", value: email, required: true, onChange: email => setEmail(email), label: "E-mail", name: "edit-mail" }),
            !newVerificationEmail ? (React.createElement("div", null,
                React.createElement(Box, { component: "div", sx: classes.notVerified }, "Din e-mail \u00E4r inte verifierad. Har du inte f\u00E5tt n\u00E5got mail?"),
                React.createElement(Button, { sx: classes.newVerifictaionEmail, onClick: sendNewVerificationMail }, "Klicka h\u00E4r f\u00F6r att skicka ett nytt"))) : (React.createElement(Box, { sx: classes.sent },
                React.createElement(SuccessIcon, { sx: classes.success }),
                React.createElement(Typography, null, "Ett nytt verifieringsmail har skickats till din email, ibland hamnar mailet i skr\u00E4pposten."))),
            React.createElement(Divider, { sx: classes.divider }),
            user && !user.hasStrongIdentification && (React.createElement(React.Fragment, null,
                ' ',
                React.createElement(PasswordField, { autoComplete: "off", label: "Nuvarande l\u00F6senord", value: currentPassword, placeholder: "Fyll i nuvarande l\u00F6senord", onChange: value => {
                        if (value !== currentPassword) {
                            setCurrentPassword(value);
                        }
                    } }),
                React.createElement(PasswordField, { autoComplete: "off", label: "Nytt l\u00F6senord", placeholder: "Fyll i nytt l\u00F6senord", value: newPassword, onChange: value => {
                        if (value !== newPassword) {
                            setNewPassword(value);
                        }
                    } }),
                React.createElement(PasswordField, { autoComplete: "off", label: "Bekr\u00E4fta nytt l\u00F6senord", placeholder: "Bekr\u00E4fta nytt l\u00F6senord", value: confirmNewPassword, helperText: (confirmNewPassword !== '' &&
                        confirmNewPassword !== newPassword &&
                        'Lösenorden matchar inte') ||
                        undefined, onChange: value => {
                        if (value !== confirmNewPassword) {
                            setConfirmNewPassword(value);
                        }
                    } }))),
            React.createElement(Box, { component: "div", sx: classes.buttonContainer }, (saving && React.createElement(Loading, null)) || (React.createElement(React.Fragment, null,
                React.createElement(Button, { to: "/mypages", variant: "outlined", size: "large", component: buttonLink, sx: classes.button }, isFormValid ? 'Avbryt' : 'Tillbaka'),
                React.createElement(Button, { variant: "contained", disabled: !isFormValid, onClick: handleSave, size: "large", sx: classes.button, className: `${classes.save} ${isFormValid && classes.active} ` }, "Spara")))),
            React.createElement(Divider, { sx: classes.divider }),
            React.createElement(CreditCard, null)))));
};
export default EditAccount;
